import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  getAllCommunityTasks,
  getCommunityTasksByType,
  getUserCreatedTasks,
  deleteCommunityTask,
  rateCommunityTask,
  reportCommunityTask,
  getFeaturedTasks,
  searchCommunityTasks,
  unifiedSearchCommunityTasks,
  placeTaskOnMap
} from '../services/api';
import CreateCommunityTask from './CreateCommunityTask';
import EditCommunityTask from './EditCommunityTask';
import CommunityTaskDetail from './CommunityTaskDetail';
import './Community.css';

const Community = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [featuredTasks, setFeaturedTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskToEdit, setTaskToEdit] = useState(null);
  const [viewingMyTasks, setViewingMyTasks] = useState(true);
  const [showReportModal, setShowReportModal] = useState(false);
  const [taskToReport, setTaskToReport] = useState(null);
  const [reportReason, setReportReason] = useState('inappropriate');
  const [reportDescription, setReportDescription] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMode, setSearchMode] = useState('unified'); // 'unified' or 'specific'
  const [searchType, setSearchType] = useState('title'); // 'title' or 'username' (for specific mode)
  const [searchLoading, setSearchLoading] = useState(false);
  const [showingSearchResults, setShowingSearchResults] = useState(false);

  // Always set to view My Tasks
  useEffect(() => {
    setViewingMyTasks(true);
    sessionStorage.setItem('community_viewing_my_tasks', 'true');
  }, []);

  // No longer loading featured tasks since we only show "My Tasks"

  // Fetch tasks based on current filter
  useEffect(() => {
    const fetchTasks = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Always show user's created tasks only
        let tasksData = await getUserCreatedTasks();
        
        // Ensure the createdBy field has the current user's information
        if (tasksData && tasksData.length > 0) {
          tasksData = tasksData.map(task => {
            // If createdBy is just an ID or doesn't have username, add current user info
            if (typeof task.createdBy === 'string' || !task.createdBy.username) {
              return {
                ...task,
                createdBy: {
                  _id: user._id,
                  username: user.username
                }
              };
            }
            return task;
          });
        }
        
        setTasks(tasksData);
      } catch (err) {
        console.error('Error fetching community tasks:', err);
        setError('Failed to load community tasks. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchTasks();
  }, [filter, viewingMyTasks, user._id, user.username]);

  // Handle task type filter change
  const handleFilterChange = (type) => {
    setFilter(type);
    setViewingMyTasks(false);
    // Clear the session storage
    sessionStorage.removeItem('community_viewing_my_tasks');
  };
  
  // Handle view my created tasks
  const handleViewMyTasks = () => {
    setViewingMyTasks(true);
    // Store the state in session storage to persist it
    sessionStorage.setItem('community_viewing_my_tasks', 'true');
  };

  // Handle search
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      return;
    }

    try {
      setSearchLoading(true);
      setError(null);
      
      let response;
      
      if (searchMode === 'unified') {
        // Use unified search - searches both usernames and task titles
        response = await unifiedSearchCommunityTasks(searchQuery.trim(), 50);
      } else {
        // Use specific search mode for backwards compatibility
        if (searchType === 'username') {
          response = await searchCommunityTasks(searchQuery.trim(), null, 50);
        } else {
          response = await searchCommunityTasks(null, searchQuery.trim(), 50);
        }
      }
      
      setTasks(response.tasks || []);
      setShowingSearchResults(true);
      
      if (response.tasks?.length === 0) {
        if (searchMode === 'unified') {
          setError(`No tasks found matching "${searchQuery.trim()}" in usernames or task titles`);
        } else {
          setError(response.message || 'No tasks found matching your search');
        }
      }
    } catch (err) {
      console.error('Error searching tasks:', err);
      setError('Failed to search tasks');
      setTasks([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Clear search and return to normal view
  const clearSearch = () => {
    setSearchQuery('');
    setShowingSearchResults(false);
    setError(null);
    // Reload the normal task list based on current filter
    if (viewingMyTasks) {
      getUserCreatedTasks().then(data => setTasks(data));
    } else if (filter === 'all') {
      getAllCommunityTasks().then(data => setTasks(data.filter(task => !task.privateTask)));
    } else {
      getCommunityTasksByType(filter).then(data => setTasks(data.filter(task => !task.privateTask)));
    }
  };
  
  // Handle task creation
  const handleTaskCreated = () => {
    setShowCreateModal(false);
    // Re-fetch tasks to include the new one
    if (viewingMyTasks) {
      getUserCreatedTasks().then(data => setTasks(data));
    } else if (filter === 'all') {
      getAllCommunityTasks().then(data => setTasks(data));
    } else {
      getCommunityTasksByType(filter).then(data => setTasks(data));
    }
  };
  
  // Handle edit task button click
  const handleEditTask = (task, event) => {
    // Prevent event bubbling to parent elements
    if (event) {
      event.stopPropagation();
    }
    setTaskToEdit(task);
    setShowEditModal(true);
  };
  
  // Handle task update
  const handleTaskUpdated = (updatedTask) => {
    setShowEditModal(false);
    setTaskToEdit(null);
    
    // Update the task in the local state
    setTasks(prevTasks => 
      prevTasks.map(task => 
        task._id === updatedTask._id ? updatedTask : task
      )
    );
  };
  
  // Handle task deletion
  const handleDeleteTask = async (id, event) => {
    // Prevent event bubbling to parent elements
    if (event) {
      event.stopPropagation();
    }
    
    if (window.confirm('Are you sure you want to delete this task?')) {
      try {
        console.log('Deleting task with ID:', id);
        const response = await deleteCommunityTask(id);
        console.log('Delete response:', response);
        
        // Update the local tasks list to remove the deleted task
        setTasks(prevTasks => prevTasks.filter(task => task._id !== id));
        
        // Force refresh of user tasks if needed
        if (viewingMyTasks) {
          // Optionally refresh from server after a short delay
          setTimeout(async () => {
            try {
              const freshTasks = await getUserCreatedTasks();
              setTasks(freshTasks);
            } catch (refreshErr) {
              console.error('Error refreshing tasks after delete:', refreshErr);
            }
          }, 500);
        }
      } catch (err) {
        console.error('Error deleting task:', err);
        alert('Failed to delete task. Please try again.');
      }
    }
  };
  
  // Handle place task on map
  const handlePlaceOnMap = (task, event) => {
    // Prevent event bubbling to parent elements
    if (event) {
      event.stopPropagation();
    }
    
    // Redirect to Hack the Planet with task placement mode
    const mapUrl = `/hack-the-planet?place-task=${task._id}`;
    
    // Store task info in sessionStorage for the map component
    sessionStorage.setItem('taskToPlace', JSON.stringify({
      _id: task._id,
      title: task.title,
      type: task.type,
      difficulty: task.difficulty
    }));
    
    // Open in new tab or navigate to map
    window.open(mapUrl, '_blank');
  };
  
  // Handle task rating
  const handleRateTask = async (id, rating) => {
    try {
      const response = await rateCommunityTask(id, rating);
      
      // Update the task in the local state
      setTasks(tasks.map(task => 
        task._id === id 
          ? { 
              ...task, 
              upvotes: response.upvotes, 
              downvotes: response.downvotes 
            } 
          : task
      ));
    } catch (err) {
      console.error('Error rating task:', err);
    }
  };
  
  // Handle task click to open detail view
  const handleTaskClick = (task) => {
    setSelectedTask(task);
    setShowDetail(true);
  };
  
  // Close the task detail view
  const handleCloseDetail = () => {
    setSelectedTask(null);
    setShowDetail(false);
  };
  
  // Handle task completion (update stats after solving)
  const handleTaskCompleted = (taskId) => {
    setTasks(tasks.map(task => 
      task._id === taskId 
        ? { ...task, completions: task.completions + 1 } 
        : task
    ));
  };
  
  // Handle report task button click
  const handleReportClick = (task, e) => {
    // Prevent event from bubbling up to parent elements
    if (e) {
      e.stopPropagation();
    }
    setTaskToReport(task);
    setShowReportModal(true);
  };
  
  // Handle report submission
  const handleSubmitReport = async () => {
    if (!taskToReport) return;
    
    try {
      const response = await reportCommunityTask(
        taskToReport._id,
        reportReason,
        reportDescription
      );
      
      // Show success message
      alert(response.message);
      
      // If task was auto-removed, update the UI
      if (response.autoRemoved) {
        setTasks(prevTasks => prevTasks.filter(task => task._id !== taskToReport._id));
      }
      
      // Clean up
      setShowReportModal(false);
      setTaskToReport(null);
      setReportReason('inappropriate');
      setReportDescription('');
    } catch (error) {
      console.error('Error reporting task:', error);
      alert('Failed to report task. Please try again.');
    }
  };

  return (
    <div className="community-container">
      <div className="community-header">
        <h1 className="community-title">
          My Tasks
        </h1>
        <div className="community-actions">
          <button 
            className="community-create-btn"
            onClick={() => setShowCreateModal(true)}
          >
            <span>+</span> Create Task (100 WR)
          </button>
        </div>
      </div>
      
      {/* Search section removed - users discover tasks through map exploration */}
      
      {/* Filter section removed - users discover tasks through map exploration */}
      
      
      {loading ? (
        <div className="loading">Loading community tasks...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : (
        <div className="community-task-list">
          {tasks.length === 0 ? (
            <div className="community-discovery-info">
              <div>
                <h3>📋 My Tasks</h3>
                <p>You haven't created any community tasks yet.</p>
                <p>Click "Create Task" above to create your first cybersecurity challenge!</p>
                <div style={{margin: '20px 0', padding: '15px', background: 'rgba(0, 255, 65, 0.1)', border: '1px solid var(--hacker-green)', borderRadius: '8px'}}>
                  <h4>💡 About Community Tasks:</h4>
                  <ul style={{textAlign: 'left', margin: '10px 0', paddingLeft: '20px'}}>
                    <li>Create custom cybersecurity challenges for others to solve</li>
                    <li>Place your tasks on the map for discovery</li>
                    <li>Tasks can only be found by other users through map exploration</li>
                    <li>Earn coins when others attempt your tasks</li>
                  </ul>
                </div>
              </div>
            </div>
          ) : (
            tasks.map(task => (
              <div className="community-task-card" key={task._id}>
                <div className="community-task-header">
                  <h3 className="community-task-title">{task.title}</h3>
                  <div className="community-task-meta">
                    <span className="community-task-type">{task.type}</span>
                    <span className="community-task-creator">
                      by {task.createdBy.username || 'Anonymous'}
                    </span>
                  </div>
                </div>
                
                <div className="community-task-description">
                  {task.description}
                </div>
                
                <div className="community-task-stats">
                  <div className="community-task-rating">
                    <div 
                      className="community-thumbs"
                      onClick={() => handleRateTask(task._id, true)}
                    >
                      <span>👍</span> {task.upvotes || 0}
                    </div>
                    <div 
                      className="community-thumbs"
                      onClick={() => handleRateTask(task._id, false)}
                    >
                      <span>👎</span> {task.downvotes || 0}
                    </div>
                  </div>
                  
                  <div className="community-task-attempts">
                    <span>💯</span> {task.completions || 0}/{task.attempts || 0}
                  </div>
                </div>
                
                {/* Task Actions */}
                <div className="community-task-actions">
                  {/* Primary Action - Solve Challenge */}
                  <div className="task-action-row">
                    <button 
                      className="solve-challenge-btn"
                      onClick={() => handleTaskClick(task)}
                    >
                      🎯 Solve Challenge
                    </button>
                  </div>
                  
                  {/* Task Status Info */}
                  <div className="task-status-info">
                    Status: {task.isLocationBased ? '📍 Placed on Map' : '📋 Ready to Place'}
                    {task.isLocationBased && task.weeklyMovementUsed && ' (Moved this week)'}
                  </div>
                  
                  {/* Map Management */}
                  <div className="task-action-row">
                    {!task.isLocationBased && (
                      <button 
                        className="map-action-btn place-btn"
                        onClick={(e) => handlePlaceOnMap(task, e)}
                        title="Place this task on the map for users to find"
                      >
                        📍 Place on Map
                      </button>
                    )}
                    
                    {task.isLocationBased && !task.weeklyMovementUsed && (
                      <button 
                        className="map-action-btn move-btn"
                        onClick={(e) => handlePlaceOnMap(task, e)}
                        title="Move this task to a new location on the map (weekly limit)"
                      >
                        🔄 Move on Map
                      </button>
                    )}
                  </div>
                  
                  {/* Task Management */}
                  <div className="task-action-row">
                    <button 
                      className="task-edit-btn"
                      onClick={(e) => handleEditTask(task, e)}
                    >
                      ✏️ Edit Task (20 WR)
                    </button>
                    <button 
                      className="task-delete-btn"
                      onClick={(e) => handleDeleteTask(task._id, e)}
                    >
                      🗑️ Delete Task
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
      
      {/* Create Task Modal */}
      {showCreateModal && (
        <CreateCommunityTask 
          onClose={() => setShowCreateModal(false)} 
          onTaskCreated={handleTaskCreated}
        />
      )}
      
      {/* Edit Task Modal */}
      {showEditModal && taskToEdit && (
        <EditCommunityTask 
          task={taskToEdit}
          onClose={() => {
            setShowEditModal(false);
            setTaskToEdit(null);
          }} 
          onTaskUpdated={handleTaskUpdated}
        />
      )}
      
      {/* Task Detail Modal */}
      {showDetail && selectedTask && (
        <CommunityTaskDetail 
          task={selectedTask} 
          onClose={handleCloseDetail}
          onTaskCompleted={() => handleTaskCompleted(selectedTask._id)}
        />
      )}
      
      {/* Report Task Modal */}
      {showReportModal && taskToReport && (
        <div className="modal-overlay" onClick={() => {
          setShowReportModal(false);
          setTaskToReport(null);
        }}>
          <div className="modal-content report-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Report Task</h2>
              <button 
                className="close-button"
                onClick={() => {
                  setShowReportModal(false);
                  setTaskToReport(null);
                }}
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '15px',
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  color: '#999',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Close"
              >
                ×
              </button>
            </div>
            <p>Report <strong>{taskToReport.title}</strong> for inappropriate content:</p>
            
            <div className="report-form">
              <div className="form-group">
                <label>Reason:</label>
                <select 
                  value={reportReason} 
                  onChange={(e) => setReportReason(e.target.value)}
                >
                  <option value="inappropriate">Inappropriate Content</option>
                  <option value="spam">Spam</option>
                  <option value="offensive">Offensive Language</option>
                  <option value="copyright">Copyright Violation</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Description (optional):</label>
                <textarea
                  value={reportDescription}
                  onChange={(e) => setReportDescription(e.target.value)}
                  placeholder="Please provide additional details about your report..."
                  rows={4}
                />
              </div>
              
              <div className="modal-buttons">
                <button onClick={handleSubmitReport} className="submit-button">
                  Submit Report
                </button>
                <button onClick={() => {
                  setShowReportModal(false);
                  setTaskToReport(null);
                }} className="cancel-button">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Community;